import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { SidebarTrigger } from "@/components/ui/sidebar";
import bizMatchLogo from "@/assets/bizmatch-logo-final.jpg";

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isBrokerPortal = location.pathname === "/broker-portal";

  // Debug: Log user state
  console.log('Header - User state:', user ? 'Logged in' : 'Not logged in', user?.email);

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
      navigate("/");
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["features", "how-it-works", "pricing"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle hash navigation when arriving at home page
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && window.location.pathname === '/') {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, []);

  const scrollToSection = (sectionId: string) => {
    // Check if we're on the home page
    const isHomePage = window.location.pathname === '/';
    
    if (isHomePage) {
      // If on home page, scroll to section directly
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // If not on home page, navigate to home page with hash
      navigate(`/#${sectionId}`);
    }
    setIsMenuOpen(false);
  };


  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm shadow-soft overflow-visible">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-32 overflow-visible gap-8">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center z-10 relative shrink-0">
            <img 
              src={bizMatchLogo} 
              alt="BizMatch Logo" 
              className="h-28 w-auto hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Mobile Buttons Container - Right Side */}
          <div className="md:hidden flex items-center gap-2">
            {isBrokerPortal && (
              <SidebarTrigger className="h-20 w-20 bg-background shadow-lg border border-border rounded-md" />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-20 w-20"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-10 w-10" /> : <Menu className="h-10 w-10" />}
            </Button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 flex-1">
            <Link 
              to="/"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Home
            </Link>
            <Link 
              to="/swipe"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Browse Businesses
            </Link>
            <Link 
              to="/franchises"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Franchises
            </Link>
            <Link 
              to="/broker-portal"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Broker Portal
            </Link>
            <button 
              onClick={() => scrollToSection("features")}
              className={`transition-colors ${
                activeSection === "features" 
                  ? "text-primary font-medium" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Features
            </button>
            <button 
              onClick={() => scrollToSection("how-it-works")}
              className={`transition-colors ${
                activeSection === "how-it-works" 
                  ? "text-primary font-medium" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              How It Works
            </button>
            <button 
              onClick={() => scrollToSection("pricing")}
              className={`transition-colors ${
                activeSection === "pricing" 
                  ? "text-primary font-medium" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Pricing
            </button>
            <Link 
              to="/contact"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </Link>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {user ? (
              <>
                <Link 
                  to="/favorites"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Favorites
                </Link>
                <NotificationCenter />
                <Button asChild variant="ghost" size="sm">
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
                <Button onClick={handleLogout} variant="outline" size="sm">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost">
                  <Link to="/sign-in">Sign In</Link>
                </Button>
                <Button asChild>
                  <Link to="/sign-up">Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </div>

          {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in-up">
            <nav className="flex flex-col gap-4 items-start">
              <Link 
                to="/"
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/swipe"
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Browse Businesses
              </Link>
              <Link 
                to="/franchises"
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Franchises
              </Link>
              {user && (
                <Link 
                  to="/favorites"
                  className="text-left text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Favorites
                </Link>
              )}
              <Link 
                to="/broker-portal"
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Broker Portal
              </Link>
              <button 
                onClick={() => scrollToSection("features")}
                className={`text-left transition-colors ${
                  activeSection === "features" 
                    ? "text-primary font-medium" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection("how-it-works")}
                className={`text-left transition-colors ${
                  activeSection === "how-it-works" 
                    ? "text-primary font-medium" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection("pricing")}
                className={`text-left transition-colors ${
                  activeSection === "pricing" 
                    ? "text-primary font-medium" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Pricing
              </button>
              <Link 
                to="/contact"
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <div className="flex flex-col gap-2 mt-4">
                {user ? (
                  <>
                    <div className="pb-2">
                      <NotificationCenter />
                    </div>
                    <Button asChild variant="ghost" className="justify-start w-full">
                      <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
                    </Button>
                    <Button onClick={() => { handleLogout(); setIsMenuOpen(false); }} variant="outline" className="justify-start w-full">
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="ghost" className="justify-start w-full">
                      <Link to="/sign-in" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
                    </Button>
                    <Button asChild className="w-full">
                      <Link to="/sign-up" onClick={() => setIsMenuOpen(false)}>Get Started</Link>
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}